/**
 * Shell de la aplicación: elegir vehículo y período, y pedir el análisis.
 *
 * Una feature no importa de otra (constitución, Art. 8): el selector de
 * vehículos y el informe se componen aquí, en `app/`, que es quien puede.
 *
 * El aspecto sigue la identidad de GuardIA —azul oscuro para la navegación,
 * blanco y azul muy claro alternados para el contenido— pero la lógica es la
 * misma de antes: los mismos permisos, el mismo período compartido entre
 * pestañas y las mismas llamadas al servidor.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchCurrentUser, fetchVehicles, logout, requestAnalysis, ApiError } from "@web/lib/api-client.ts";
import { Button, Field, Notice } from "@web/components/primitives.tsx";
import { BrandLockup, GuardiaLogo } from "@web/components/brand.tsx";
import { DrivingReport } from "@web/features/analysis/driving-report.tsx";
import { DownloadReport } from "@web/features/analysis/download-report.tsx";
import { FleetPanel } from "@web/features/fleet/fleet-panel.tsx";
import { CalibrationPanel } from "@web/features/calibration/calibration-panel.tsx";
import { CohortsPanel } from "@web/features/cohorts/cohorts-panel.tsx";
import { AdminPanel } from "@web/features/admin/admin-panel.tsx";
import { RastreoPanel } from "@web/features/rastreo/rastreo-panel.tsx";
import { Login } from "@web/features/auth/login.tsx";
import { bogotaDateInputValue, dateInputToBogotaIso } from "@core/pipeline/analysis-period.ts";

/**
 * Decide qué se muestra: la entrada o la aplicación.
 *
 * La sesión la resuelve el servidor, no el navegador. Aquí solo se pregunta
 * «¿quién soy?»: si contesta 401, no hay sesión y se muestra la entrada. No hay
 * ninguna bandera en el cliente que se pueda cambiar para saltarse esto — la
 * API vuelve a exigir sesión en cada petición.
 */
export function App() {
  const me = useQuery({
    queryKey: ["me"],
    queryFn: fetchCurrentUser,
    retry: false,
  });

  if (me.isPending) return <BootScreen />;
  if (me.isError) return <Login />;

  return (
    <Workspace
      email={me.data.email}
      can={me.data.can}
      gpswoxLinked={me.data.gpswoxLinked}
    />
  );
}

/** Estado de carga inicial. Vacío no: se dice que está cargando. */
function BootScreen() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-gd-deep px-6">
      <GuardiaLogo className="h-10" />
      <p className="text-[15px] text-white/70" role="status">
        Cargando GuardIA Driving…
      </p>
    </div>
  );
}

type Tab = "vehiculo" | "flota" | "rastreo" | "cohortes" | "laboratorio" | "usuarios";

type Permissions = {
  calibrate: boolean;
  administer: boolean;
  seeCohorts: boolean;
  seeFleet: boolean;
  seeRastreo: boolean;
};

function Workspace({
  email,
  can,
  gpswoxLinked,
}: {
  email: string;
  can: Permissions;
  gpswoxLinked: boolean;
}) {
  const queryClient = useQueryClient();
  const today = bogotaDateInputValue(new Date());
  const [tab, setTab] = useState<Tab>("vehiculo");
  const [deviceId, setDeviceId] = useState("");
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [menuOpen, setMenuOpen] = useState(false);

  // Un rango al revés no se manda al servidor: se avisa aquí.
  const rangeIsBackwards = from > to;

  // El período es el mismo para las tres pestañas: comparar dos vistas de
  // rangos distintos es el error que más fácil se comete y más caro sale.
  const fromIso = dateInputToBogotaIso(from, "00:00");
  const toIso = dateInputToBogotaIso(to, "23:59");
  const period = !rangeIsBackwards && fromIso && toIso ? { fromIso, toIso } : null;

  // Los permisos los decide el servidor y aquí solo se obedecen, para no
  // ofrecer pestañas que van a devolver 403. Cada endpoint los vuelve a exigir:
  // esto es comodidad, no seguridad (constitución, Art. 4).
  const tabs: { id: Tab; label: string }[] = [
    { id: "vehiculo", label: "Un vehículo" },
    ...(can.seeFleet ? [{ id: "flota" as const, label: "Flota" }] : []),
    ...(can.seeRastreo ? [{ id: "rastreo" as const, label: "Rastreo" }] : []),
    ...(can.seeCohorts ? [{ id: "cohortes" as const, label: "Cohortes" }] : []),
    ...(can.calibrate ? [{ id: "laboratorio" as const, label: "Laboratorio" }] : []),
    ...(can.administer ? [{ id: "usuarios" as const, label: "Usuarios" }] : []),
  ];

  const vehicles = useQuery({ queryKey: ["vehicles"], queryFn: fetchVehicles });

  const signOut = useMutation({
    mutationFn: logout,
    onSuccess: () => queryClient.clear(),
  });

  const analysis = useMutation({
    mutationFn: () => {
      // El rango cubre los días completos: desde las 00:00 del primero hasta
      // las 23:59 del último, en hora de Bogotá.
      if (!period) throw new ApiError("INVALID_PERIOD", "La fecha no es válida.", 400);
      return requestAnalysis({ deviceId, ...period });
    },
  });

  // Escape cierra el menú móvil. Abrir algo que solo se cierra apuntándole con
  // el dedo deja fuera a quien navega con teclado.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const goTo = (next: Tab) => {
    setTab(next);
    setMenuOpen(false);
  };

  // Rastreo es una consola —lista a la izquierda, mapa a la derecha, como Traccar—
  // y ocupa todo el alto bajo la barra, sin la barra de fechas ni el pie: el
  // período se elige dentro de sus Informes, con este mismo estado compartido.
  const rastreoConsole = tab === "rastreo" && can.seeRastreo;

  return (
    <div className={`flex flex-col bg-gd-mist ${rastreoConsole ? "h-dvh overflow-hidden" : "min-h-dvh"}`}>
      <a
        href="#contenido"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:font-semibold focus:text-gd-navy"
      >
        Saltar al contenido
      </a>

      <AppHeader
        email={email}
        tabs={tabs}
        activeTab={tab}
        onNavigate={goTo}
        onSignOut={() => signOut.mutate()}
        signingOut={signOut.isPending}
        menuOpen={menuOpen}
        onToggleMenu={() => setMenuOpen((open) => !open)}
      />

      {rastreoConsole ? (
        <main id="contenido" className="flex min-h-0 w-full flex-1">
          <RastreoPanel
            period={period}
            dates={{ from, to, today, onFrom: setFrom, onTo: setTo, rangeIsBackwards }}
            onSignOut={() => signOut.mutate()}
            signingOut={signOut.isPending}
          />
        </main>
      ) : (
        <>
          <main
            id="contenido"
            className={`mx-auto w-full flex-1 px-4 py-7 sm:px-6 sm:py-9 ${
              tab === "vehiculo" ? "max-w-4xl" : "max-w-7xl"
            }`}
          >
            <div className="space-y-6">
              {!gpswoxLinked && tab !== "rastreo" ? (
                <Notice title="Tu usuario no está vinculado con WOX">
                  Hasta que un administrador verifique el vínculo no vas a ver ningún vehículo. No es un
                  error de la aplicación: es el cierre por defecto.
                </Notice>
              ) : null}

              <FilterBar
                showVehicle={tab === "vehiculo"}
                vehicles={vehicles.data?.vehicles ?? []}
                vehiclesLoading={vehicles.isPending}
                deviceId={deviceId}
                onDeviceId={setDeviceId}
                from={from}
                to={to}
                today={today}
                onFrom={setFrom}
                onTo={setTo}
                rangeIsBackwards={rangeIsBackwards}
                analyzing={analysis.isPending}
                onSubmit={() => {
                  if (deviceId) analysis.mutate();
                }}
              />

              {vehicles.isError && tab !== "rastreo" ? (
                <Notice tone="warning" title="No se pudo cargar la flota">
                  {vehicles.error instanceof ApiError ? vehicles.error.message : "Error inesperado."}
                </Notice>
              ) : null}

              {tab !== "rastreo" && vehicles.data && vehicles.data.total === 0 ? (
                <Notice title="No hay vehículos disponibles">
                  Tu usuario no tiene vehículos autorizados en la plataforma WOX.
                </Notice>
              ) : null}

              {tab === "vehiculo" ? (
                <>
                  {analysis.isError ? (
                    <Notice tone="warning" title="No se pudo generar el análisis">
                      {analysis.error instanceof ApiError ? analysis.error.message : "Error inesperado."}
                    </Notice>
                  ) : null}

                  {analysis.isPending ? (
                    <Notice title="Analizando el período">
                      Se está trayendo el historial del equipo y corriendo el motor. En períodos largos
                      puede tardar unos segundos.
                    </Notice>
                  ) : null}

                  {analysis.data ? (
                    <>
                      <DownloadReport result={analysis.data} />
                      <DrivingReport result={analysis.data} />
                    </>
                  ) : null}

                  {!analysis.data && !analysis.isPending && !analysis.isError ? (
                    <EmptyState
                      title="Todavía no hay un análisis"
                      body="Elige un vehículo y un período, y pulsa «Analizar». El informe se calcula sobre el historial real del equipo, sin estimaciones."
                    />
                  ) : null}
                </>
              ) : null}

              {tab === "flota" && can.seeFleet ? <FleetPanel period={period} /> : null}
              {tab === "cohortes" && can.seeCohorts ? <CohortsPanel period={period} /> : null}
              {tab === "laboratorio" && can.calibrate ? (
                <CalibrationPanel period={period} vehicles={vehicles.data?.vehicles ?? []} />
              ) : null}
              {tab === "usuarios" && can.administer ? <AdminPanel /> : null}
            </div>
          </main>

          <footer className="border-t border-gd-line bg-white px-4 py-5 text-center text-[13px] text-gd-muted sm:px-6">
            GuardIA Driving · Análisis de conducción determinístico · Pipeline V5
          </footer>
        </>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- *
 * Header
 * --------------------------------------------------------------- */

/**
 * Barra superior de la aplicación.
 *
 * Es la de una herramienta, no la de una página comercial: alta compacta, a
 * todo el ancho y sin banner. Toma de guardiasync.com el azul oscuro, el cian
 * de acento y el logotipo, y nada más.
 */
function AppHeader({
  email,
  tabs,
  activeTab,
  onNavigate,
  onSignOut,
  signingOut,
  menuOpen,
  onToggleMenu,
}: {
  email: string;
  tabs: { id: Tab; label: string }[];
  activeTab: Tab;
  onNavigate: (tab: Tab) => void;
  onSignOut: () => void;
  signingOut: boolean;
  menuOpen: boolean;
  onToggleMenu: () => void;
}) {
  return (
    <header className="sticky top-0 z-40 w-full bg-gd-deep">
      {/* Hilo cian: el único adorno luminoso, y muy sutil. */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-gd-cyan/70 to-transparent" />

      <div className="mx-auto flex w-full max-w-[1600px] items-center gap-4 px-4 py-3 sm:px-6">
        <BrandLockup />

        {/* Navegación de escritorio */}
        <nav aria-label="Secciones" className="ml-4 hidden flex-1 items-center gap-1 lg:flex">
          {tabs.map((item) => {
            const active = item.id === activeTab;
            return (
              <button
                key={item.id}
                type="button"
                aria-current={active ? "page" : undefined}
                onClick={() => onNavigate(item.id)}
                className={`relative rounded-lg px-3 py-2 text-[15px] transition-colors ${
                  active
                    ? "font-semibold text-white"
                    : "text-white/65 hover:bg-white/5 hover:text-white"
                }`}
              >
                {item.label}
                {/* El estado activo se marca con cian Y con el peso del texto:
                    no depende solo del color. */}
                {active ? (
                  <span
                    aria-hidden="true"
                    className="absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-gd-cyan"
                  />
                ) : null}
              </button>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <div className="hidden text-right md:block">
            <p className="max-w-[220px] truncate text-[13px] text-white/60">{email}</p>
            <button
              type="button"
              onClick={onSignOut}
              disabled={signingOut}
              className="text-[13px] font-semibold text-gd-cyan underline-offset-4 hover:underline disabled:opacity-60"
            >
              {signingOut ? "Saliendo…" : "Cerrar sesión"}
            </button>
          </div>

          {/* Botón de menú móvil: 44 px de lado, cómodo para el pulgar. */}
          <button
            type="button"
            onClick={onToggleMenu}
            aria-expanded={menuOpen}
            aria-controls="menu-movil"
            className="flex size-11 items-center justify-center rounded-xl border border-white/15 text-white lg:hidden"
          >
            <span className="sr-only">{menuOpen ? "Cerrar menú" : "Abrir menú"}</span>
            <span aria-hidden="true" className="relative block h-3.5 w-5">
              <span
                className={`absolute inset-x-0 top-0 h-0.5 rounded bg-current transition-transform ${
                  menuOpen ? "translate-y-1.5 rotate-45" : ""
                }`}
              />
              <span
                className={`absolute inset-x-0 top-1.5 h-0.5 rounded bg-current transition-opacity ${
                  menuOpen ? "opacity-0" : ""
                }`}
              />
              <span
                className={`absolute inset-x-0 top-3 h-0.5 rounded bg-current transition-transform ${
                  menuOpen ? "-translate-y-1.5 -rotate-45" : ""
                }`}
              />
            </span>
          </button>
        </div>
      </div>

      {/* Menú móvil */}
      {menuOpen ? (
        <div id="menu-movil" className="border-t border-white/10 bg-gd-navy px-4 pb-4 pt-2 lg:hidden">
          <nav aria-label="Secciones" className="flex flex-col">
            {tabs.map((item) => {
              const active = item.id === activeTab;
              return (
                <button
                  key={item.id}
                  type="button"
                  aria-current={active ? "page" : undefined}
                  onClick={() => onNavigate(item.id)}
                  className={`flex min-h-12 items-center gap-3 rounded-xl px-3 text-left text-[15px] ${
                    active ? "bg-white/10 font-semibold text-white" : "text-white/75"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`h-5 w-1 rounded-full ${active ? "bg-gd-cyan" : "bg-transparent"}`}
                  />
                  {item.label}
                </button>
              );
            })}
          </nav>

          <div className="mt-3 border-t border-white/10 pt-3">
            <p className="truncate text-[13px] text-white/60">{email}</p>
            <button
              type="button"
              onClick={onSignOut}
              disabled={signingOut}
              className="mt-1 min-h-11 text-[15px] font-semibold text-gd-cyan disabled:opacity-60"
            >
              {signingOut ? "Saliendo…" : "Cerrar sesión"}
            </button>
          </div>
        </div>
      ) : null}
    </header>
  );
}

/* --------------------------------------------------------------- *
 * Filtros
 * --------------------------------------------------------------- */

/**
 * Vehículo y período, agrupados.
 *
 * En celular cae a una columna y el botón ocupa el ancho. Las etiquetas son
 * elementos propios —no placeholders— y el error del rango sale debajo del
 * campo, no en otra parte de la pantalla.
 */
function FilterBar({
  showVehicle,
  vehicles,
  vehiclesLoading,
  deviceId,
  onDeviceId,
  from,
  to,
  today,
  onFrom,
  onTo,
  rangeIsBackwards,
  analyzing,
  onSubmit,
}: {
  showVehicle: boolean;
  vehicles: { deviceId: string; plate: string | null; name: string | null; group: string | null }[];
  vehiclesLoading: boolean;
  deviceId: string;
  onDeviceId: (value: string) => void;
  from: string;
  to: string;
  today: string;
  onFrom: (value: string) => void;
  onTo: (value: string) => void;
  rangeIsBackwards: boolean;
  analyzing: boolean;
  onSubmit: () => void;
}) {
  return (
    <form
      className="gd-card p-5 sm:p-6"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
        {showVehicle ? (
          <Field label="Vehículo" htmlFor="filtro-vehiculo">
            <select
              id="filtro-vehiculo"
              className="lg:min-w-64"
              value={deviceId}
              onChange={(event) => onDeviceId(event.target.value)}
            >
              <option value="">{vehiclesLoading ? "Cargando flota…" : "Selecciona…"}</option>
              {vehicles.map((vehicle) => (
                <option key={vehicle.deviceId} value={vehicle.deviceId}>
                  {vehicle.plate ?? vehicle.name ?? vehicle.deviceId}
                  {vehicle.group ? ` — ${vehicle.group}` : ""}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        <Field label="Desde" htmlFor="filtro-desde">
          <input
            id="filtro-desde"
            type="date"
            value={from}
            max={today}
            onChange={(event) => onFrom(event.target.value)}
          />
        </Field>

        <Field
          label="Hasta"
          htmlFor="filtro-hasta"
          error={rangeIsBackwards ? "La fecha «desde» es posterior a la fecha «hasta»." : undefined}
        >
          <input
            id="filtro-hasta"
            type="date"
            value={to}
            min={from}
            max={today}
            aria-invalid={rangeIsBackwards || undefined}
            aria-describedby={rangeIsBackwards ? "filtro-hasta-error" : undefined}
            onChange={(event) => onTo(event.target.value)}
          />
        </Field>

        {showVehicle ? (
          <div className="lg:pb-0">
            <Button type="submit" block disabled={!deviceId || rangeIsBackwards || analyzing}>
              {analyzing ? "Analizando…" : "Analizar"}
            </Button>
          </div>
        ) : null}
      </div>
    </form>
  );
}

/** Estado vacío: dice qué falta hacer, no se queda en blanco. */
function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="gd-card px-6 py-12 text-center">
      <div
        aria-hidden="true"
        /* Azul y no cian: sobre el azul muy claro el cian no alcanza a leerse. */
        className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-gd-mist font-display text-2xl font-bold text-gd-blue"
      >
        ▮
      </div>
      <h2 className="mt-4 font-display text-lg font-bold text-gd-navy">{title}</h2>
      <p className="mx-auto mt-2 max-w-md text-[15px] text-gd-muted">{body}</p>
    </div>
  );
}
